import { useState, useEffect, useCallback } from 'react';
import { Box, Button, Card, CardContent, Chip, Grid, Stack, Typography } from '@mui/joy';
import {
  AccessTime,
  Assignment,
  CalendarMonth,
  EventNote,
  PendingActions,
  Settings,
  SwapHoriz,
  Timer,
  TuneRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '@/context/AuthContext';
import { dashboardService } from '@/services/dashboardService';
import { projectService } from '@/services/projectService';
import type { EmployeeDashboardData, TaskEntry } from '@/types/dashboard';
import WelcomeHeader from '@/components/dashboard/WelcomeHeader';
import { useDashboardRefresh } from '@/components/dashboard/useDashboardRefresh';
import StatCard from '@/components/dashboard/StatCard';
import WidgetPanel from '@/components/dashboard/WidgetPanel';
import ScheduleListItem from '@/components/dashboard/ScheduleListItem';
import TaskListItem from '@/components/dashboard/TaskListItem';
import NotificationWidget from '@/components/NotificationWidget';

export default function EmployeeDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<EmployeeDashboardData | null>(null);
  const [tasks, setTasks] = useState<TaskEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setTasksLoading(true);
      const [dashData, tasksData] = await Promise.all([
        dashboardService.getEmployeeDashboard(),
        dashboardService.getMyTasks(10),
      ]);
      setData(dashData);
      setTasks(tasksData);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
      setTasksLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useDashboardRefresh(fetchData);

  const handleTaskStatusChange = async (taskId: number, newStatus: string) => {
    try {
      await projectService.updateTask(taskId, { status: newStatus as any });
      setTasks(prev => prev.filter(t => newStatus === 'completed' ? t.id !== taskId : true).map(t => t.id === taskId ? { ...t, status: newStatus } : t));
      toast.success('Task updated');
    } catch {
      toast.error('Failed to update task');
    }
  };

  // Countdown for next shift
  const getCountdown = () => {
    if (!data?.nextShift) return null;
    const start = new Date(data.nextShift.start_datetime);
    const end = new Date(data.nextShift.end_datetime);
    const now = new Date();
    if (now >= start && now <= end) return 'Currently on shift';
    const diff = start.getTime() - now.getTime();
    if (diff <= 0) return null;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `Starts in ${days} day${days > 1 ? 's' : ''}`;
    }
    return `Starts in ${hours}h ${minutes}m`;
  };

  return (
    <Box>
      <WelcomeHeader onRefresh={fetchData} />

      {/* Quick Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="title-lg" sx={{ mb: 1.5 }}>Quick Actions</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" startDecorator={<SwapHoriz />} onClick={() => navigate('/change-requests')}>
              Request Change
            </Button>
            <Button variant="outlined" startDecorator={<CalendarMonth />} onClick={() => navigate('/calendar')}>
              View My Calendar
            </Button>
            <Button variant="outlined" startDecorator={<Assignment />} onClick={() => navigate('/projects')}>
              View My Tasks
            </Button>
            <Button variant="outlined" startDecorator={<Settings />} onClick={() => navigate('/preferences')}>
              Update Preferences
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* My Next Shift */}
      <Card
        variant="soft"
        color="primary"
        sx={{ mb: 3 }}
      >
        <CardContent>
          {loading ? (
            <Typography level="body-md">Loading...</Typography>
          ) : data?.nextShift ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
              <Box>
                <Typography level="title-lg" sx={{ mb: 0.5 }}>
                  My Next Shift
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="sm"
                    sx={{
                      bgcolor: data.nextShift.shift_type.color,
                      color: 'white',
                    }}
                  >
                    {data.nextShift.shift_type.name}
                  </Chip>
                  <Typography level="body-md">
                    {new Date(data.nextShift.start_datetime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    {' \u2022 '}
                    {new Date(data.nextShift.start_datetime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    {' – '}
                    {new Date(data.nextShift.end_datetime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </Typography>
                </Stack>
              </Box>
              <Chip
                size="lg"
                variant="solid"
                color="primary"
                startDecorator={<Timer />}
              >
                {getCountdown()}
              </Chip>
            </Stack>
          ) : (
            <Box>
              <Typography level="title-lg" sx={{ mb: 0.5 }}>My Next Shift</Typography>
              <Typography level="body-md">No upcoming shifts scheduled</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={6} md={3}>
          <StatCard
            icon={<CalendarMonth sx={{ color: 'white' }} />}
            value={data?.weekStats.shifts_this_week ?? '-'}
            label="My Shifts This Week"
            color="primary.500"
            loading={loading}
          />
        </Grid>
        <Grid xs={6} md={3}>
          <StatCard
            icon={<AccessTime sx={{ color: 'white' }} />}
            value={data ? `${data.weekStats.total_hours_this_week.toFixed(1)}h` : '-'}
            label="Total Hours This Week"
            color="success.500"
            loading={loading}
          />
        </Grid>
        <Grid xs={6} md={3}>
          <StatCard
            icon={<PendingActions sx={{ color: 'white' }} />}
            value={data?.weekStats.pending_requests ?? '-'}
            label="Pending Requests"
            color="warning.500"
            loading={loading}
          />
        </Grid>
        <Grid xs={6} md={3}>
          <StatCard
            icon={<Assignment sx={{ color: 'white' }} />}
            value={data?.weekStats.open_tasks ?? '-'}
            label="My Open Tasks"
            color="info.500"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid xs={12} md={6}>
          <Stack spacing={3}>
            {/* Upcoming Schedule */}
            <WidgetPanel
              title="My Upcoming Schedule"
              loading={loading}
              isEmpty={!data?.upcomingSchedule.length}
              emptyMessage="No shifts scheduled for the next 7 days"
              onViewAll={() => navigate('/calendar')}
              viewAllLabel="View Calendar"
            >
              <Stack spacing={1}>
                {data?.upcomingSchedule.map(entry => (
                  <ScheduleListItem key={entry.id} entry={entry} showEmployee={false} />
                ))}
              </Stack>
            </WidgetPanel>

            {/* My Assigned Tasks */}
            <WidgetPanel
              title="My Assigned Tasks"
              loading={tasksLoading}
              isEmpty={!tasks.length}
              emptyMessage="No tasks assigned to you right now."
              onViewAll={() => navigate('/projects')}
              viewAllLabel="View All Tasks"
            >
              <Stack spacing={1}>
                {tasks.map(task => (
                  <TaskListItem
                    key={task.id}
                    task={task}
                    onStatusChange={handleTaskStatusChange}
                    showProject
                  />
                ))}
              </Stack>
            </WidgetPanel>
          </Stack>
        </Grid>

        {/* Right Column */}
        <Grid xs={12} md={6}>
          <Stack spacing={3}>
            {/* Change Requests */}
            <WidgetPanel
              title="My Change Requests"
              loading={loading}
              isEmpty={!data?.myChangeRequests.length}
              emptyMessage="No pending requests — all caught up!"
              onViewAll={() => navigate('/change-requests')}
              action={
                <Button
                  size="sm"
                  variant="soft"
                  startDecorator={<SwapHoriz />}
                  onClick={() => navigate('/change-requests')}
                >
                  New Request
                </Button>
              }
            >
              <Stack spacing={1}>
                {data?.myChangeRequests.map(cr => (
                  <Box
                    key={cr.id}
                    sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 'sm' }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography level="body-sm" fontWeight="lg">
                          {cr.request_type.replace('_', ' ')}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          {new Date(cr.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Chip
                        size="sm"
                        variant="soft"
                        color={
                          cr.status === 'approved' ? 'success'
                          : cr.status === 'rejected' ? 'danger'
                          : cr.status === 'pending' ? 'warning'
                          : 'neutral'
                        }
                      >
                        {cr.status}
                      </Chip>
                    </Stack>
                    {cr.review_comments && (
                      <Typography level="body-xs" color="neutral" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        "{cr.review_comments}"
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </WidgetPanel>

            {/* Preferences Status */}
            <Card>
              <CardContent>
                <Typography level="title-lg" sx={{ mb: 1 }}>My Preferences</Typography>
                {loading ? (
                  <Typography level="body-sm">Loading...</Typography>
                ) : data?.preferencesStatus ? (
                  <Stack spacing={1}>
                    <Typography level="body-md">
                      {data.preferencesStatus.categories_configured} of {data.preferencesStatus.total_categories} preference categories configured
                    </Typography>
                    {data.preferencesStatus.categories_configured < data.preferencesStatus.total_categories && (
                      <Typography level="body-sm" color="warning">
                        Set up your preferences to help your manager create better schedules
                      </Typography>
                    )}
                    <Button
                      variant="outlined"
                      size="sm"
                      startDecorator={<TuneRounded />}
                      onClick={() => navigate('/preferences')}
                    >
                      Update Preferences
                    </Button>
                  </Stack>
                ) : null}
              </CardContent>
            </Card>

            {/* Notifications */}
            <NotificationWidget maxItems={5} />
          </Stack>
        </Grid>
      </Grid>

    </Box>
  );
}
